import React, { useEffect, useState, Fragment } from 'react';

import {
    Typography, Paper, Grid, Button,
} from '@material-ui/core';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import NumberUtils from 'lib/NumberUtils';
import ModalUtils from 'utils/ModalUtils';
import PayrollUtils from 'utils/PayrollUtils';
import { FetchPolicy } from 'utils/enum/Core';
import { LocalAtmIcon } from 'components/icons';
import Loading from 'components/widgets/Loading';
import { useQuery, useLazyQuery } from '@apollo/client';
import { CheckDetailLabels } from 'utils/enum/PayrollEnum';
import PayrollChecksQuery from 'services/graphQL/query/payroll/PayrollChecks';
import PayrollCheckDialog from 'components/widgets/payroll/PayrollCheckDialog';
import UnpaidEmployeeInformation from 'components/widgets/payroll/PayrollChecks/UnpaidEmployeeInformation';

const CommissionHourlyLimitOvertimePanel = ({
    calculateCheckDetail, setCalculateCheckDetail, classes, pendingFinalPayment,
    unpaidList, hasCheck, payPeriod, employeeId, reloadEmployeeHasCheck, reloadUnpaidList,
}) => {
    const [state, setState] = useState({
        netPay: 0,
        userTax: 0,
        grossPay: 0,
        medicare: 0,
        stateTax: 0,
        hourlyRate: 0,
        checkNumber: 0,
        commissions: 0,
        hoursWorked: 0,
        overtimeRate: 0,
        userTaxName: '',
        withholdings: 0,
        preTaxBonuses: 0,
        overtimeWorked: 0,
        postTaxBonuses: 0,
        socialSecurity: 0,
        commissionList: [],
        preTaxDeductions: 0,
        postTaxDeductions: 0,
        standardTaxBonuses: 0,
        openCheckDialog: false,
        standardTaxDeductions: 0,
    });

    const {
        data, loading, error, refetch,
    } = useQuery(PayrollChecksQuery.GET_PAYROLL_CHECK_DETAIL_COMMISSION_HOURLY, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        variables: {
            payPeriod,
            employeeId,
        },
        skip: !employeeId || !payPeriod,
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data.getPayrollCheckDetail) {
            const { getPayrollCheckDetail: { check, ...rest } } = data;
            setState({
                ...rest,
                openCheckDialog: false,
                netPay: check ? check.amount : rest.netPay,
                checkNumber: check ? check.checkNumber || check.achCheckNumber : 0,
            });
        } else {
            setState({
                netPay: 0,
                userTax: 0,
                grossPay: 0,
                medicare: 0,
                stateTax: 0,
                hourlyRate: 0,
                checkNumber: 0,
                commissions: 0,
                hoursWorked: 0,
                overtimeRate: 0,
                userTaxName: '',
                withholdings: 0,
                preTaxBonuses: 0,
                overtimeWorked: 0,
                postTaxBonuses: 0,
                socialSecurity: 0,
                commissionList: [],
                preTaxDeductions: 0,
                postTaxDeductions: 0,
                standardTaxBonuses: 0,
                openCheckDialog: false,
                standardTaxDeductions: 0,
            });
        }
        setCalculateCheckDetail(false);
    }, [data, loading, error, setCalculateCheckDetail]);

    const [validatePayrollCheck, {
        loading: loadingValidations, data: validationResult, error: validationErrors,
    }] = useLazyQuery(PayrollChecksQuery.CAN_GENERATE_PAYROLL_CHECK, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });
    useEffect(() => {
        if (validationErrors) {
            ModalUtils.errorMessage(validationErrors?.graphQLErrors);
        }
        if (!loadingValidations && validationResult?.canGeneratePayrollCheck) {
            setState((previousState) => ({
                ...previousState,
                openCheckDialog: true,
            }));
        }
    }, [loadingValidations, validationResult, validationErrors]);

    useEffect(() => {
        if (calculateCheckDetail) refetch();
    }, [calculateCheckDetail, refetch]);

    const toggleCheckDialog = () => {
        if (!state.openCheckDialog) {
            validatePayrollCheck({
                variables: {
                    payPeriod,
                    employeeId,
                },
            });
        } else {
            setState((previousState) => ({
                ...previousState,
                openCheckDialog: false,
            }));
        }
    };

    const refetchData = () => {
        reloadUnpaidList();
        reloadEmployeeHasCheck();
        setCalculateCheckDetail(true);
    };

    const checkDataInput = {
        netPay: state.netPay,
        userTax: state.userTax,
        grossPay: state.grossPay,
        medicare: state.medicare,
        stateTax: state.stateTax,
        hourlyRate: state.hourlyRate,
        commissions: state.commissions,
        hoursWorked: state.hoursWorked,
        overtimeRate: state.overtimeRate,
        withholdings: state.withholdings,
        preTaxBonuses: state.preTaxBonuses,
        commissionList: state.commissionList,
        overtimeWorked: state.overtimeWorked,
        socialSecurity: state.socialSecurity,
        postTaxBonuses: state.postTaxBonuses,
        preTaxDeductions: state.preTaxDeductions,
        postTaxDeductions: state.postTaxDeductions,
        standardTaxBonuses: state.standardTaxBonuses,
        standardTaxDeductions: state.standardTaxDeductions,
    };

    const checkDetailRows = [
        { label: CheckDetailLabels.RATE_PER_HOUR, value: state.hourlyRate },
        { label: CheckDetailLabels.HOURLY_PAY, value: (state.hourlyRate * state.hoursWorked) + (state.overtimeRate * state.overtimeWorked) },
        { label: CheckDetailLabels.PRE_TAX_ADDITIONS, value: state.preTaxBonuses + state.standardTaxBonuses },
        { label: CheckDetailLabels.GROSS_PAY, value: state.grossPay + state.preTaxDeductions + state.standardTaxDeductions },
        { label: CheckDetailLabels.PRE_TAX_DEDUCTIONS, value: state.preTaxDeductions + state.standardTaxDeductions },
        { label: CheckDetailLabels.TAXABLE_GROSS, value: state.grossPay },
        { label: CheckDetailLabels.FEDERAL_WITHHOLDING, value: state.withholdings },
        { label: CheckDetailLabels.SOCIAL_SECURITY, value: state.socialSecurity },
        { label: CheckDetailLabels.MEDICARE, value: state.medicare },
        ...(state.stateTax > 0 ? [{ label: CheckDetailLabels.STATE_TAX, value: state.stateTax }] : []),
        ...(state.userTax > 0 ? [{ label: state.userTaxName, value: state.userTax }] : []),
        { label: CheckDetailLabels.POST_TAX_ADDITIONS, value: state.postTaxBonuses },
        { label: CheckDetailLabels.POST_TAX_DEDUCTIONS, value: state.postTaxDeductions },
        { label: CheckDetailLabels.NET_PAY, value: state.netPay },
    ];
    const isBoldText = (text) => text === CheckDetailLabels.GROSS_PAY
    || text === CheckDetailLabels.TAXABLE_GROSS
    || text === CheckDetailLabels.NET_PAY;

    return (
        <div className={classes.root}>
            <Grid item className={classes.header}>
                <Typography variant="h6" className={classes.title}>Commission + Hourly</Typography>
                {pendingFinalPayment && (
                    <UnpaidEmployeeInformation list={unpaidList} />
                )}
            </Grid>
            <Paper elevation={0} className={classes.boxRow} variant="outlined" square>
                {loading && (
                    <div className={classes.loadingIndicator}>
                        <Loading />
                    </div>
                )}
                {!loading && (
                    <Grid container spacing={1}>
                        {hasCheck && (
                            <>
                                <Grid item xs={7} className="content-label">
                                    <Typography color="primary" variant="h6">{CheckDetailLabels.CHECK_NUMBER}</Typography>
                                </Grid>
                                <Grid item xs={5} className="content-value">
                                    <Typography>{state.checkNumber}</Typography>
                                </Grid>

                            </>
                        )}
                        <Grid item xs={7} className="content-label">
                            <Typography color="primary" variant="h6">{CheckDetailLabels.APPROVED_COMMISSIONS}</Typography>
                        </Grid>
                        <Grid item xs={5} className="content-value">
                            <Typography>{NumberUtils.applyCurrencyFormat(state.commissions)}</Typography>
                        </Grid>
                        <Grid item xs={7} className="content-label">
                            <Typography color="primary" variant="h6">{CheckDetailLabels.APPROVED_HOURS}</Typography>
                        </Grid>
                        <Grid item xs={2} className="content-value">
                            <Typography>{PayrollUtils.getHoursAndMinutesFromDecimalHours(state.hoursWorked)}</Typography>
                        </Grid>
                        <Grid item xs={3} className="content-value">
                            <Typography>{NumberUtils.applyCurrencyFormat(state.hourlyRate * state.hoursWorked)}</Typography>
                        </Grid>
                        <Grid item xs={7} className="content-label">
                            <Typography color="primary" variant="h6">{CheckDetailLabels.APPROVED_OVERTIME_HOURS}</Typography>
                        </Grid>
                        <Grid item xs={2} className="content-value">
                            <Typography>{PayrollUtils.getHoursAndMinutesFromDecimalHours(state.overtimeWorked)}</Typography>
                        </Grid>
                        <Grid item xs={3} className="content-value">
                            <Typography>{NumberUtils.applyCurrencyFormat(state.overtimeRate * state.overtimeWorked)}</Typography>
                        </Grid>
                        {checkDetailRows.map((item, index) => (
                            <Fragment key={index}>
                                <Grid item xs={7} className="content-label">
                                    <Typography
                                        variant="h6"
                                        color="primary"
                                        className={clsx({ [classes.textBold]: isBoldText(item.label) })}
                                    >
                                        {item.label}
                                    </Typography>
                                </Grid>
                                <Grid item xs={5} className="content-value">
                                    <Typography>{NumberUtils.applyCurrencyFormat(item.value)}</Typography>
                                </Grid>
                            </Fragment>
                        ))}
                        {!hasCheck && state.netPay > 0 && (
                            <Grid item xs={12} className={classes.button}>
                                <Button
                                    size="small"
                                    disabled={loading}
                                    variant="contained"
                                    onClick={toggleCheckDialog}
                                    startIcon={<LocalAtmIcon />}
                                    className={classes.newButton}
                                >
                                    Generate Check
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Paper>
            {state.openCheckDialog && (
                <PayrollCheckDialog
                    payPeriod={payPeriod}
                    refetch={refetchData}
                    employeeId={employeeId}
                    checkData={checkDataInput}
                    onClose={toggleCheckDialog}
                />
            )}
        </div>
    );
};

CommissionHourlyLimitOvertimePanel.propTypes = {
    hasCheck: PropTypes.bool.isRequired,
    classes: PropTypes.object.isRequired,
    payPeriod: PropTypes.string.isRequired,
    employeeId: PropTypes.number.isRequired,
    reloadUnpaidList: PropTypes.func.isRequired,
    pendingFinalPayment: PropTypes.bool.isRequired,
    calculateCheckDetail: PropTypes.bool.isRequired,
    reloadEmployeeHasCheck: PropTypes.func.isRequired,
    setCalculateCheckDetail: PropTypes.func.isRequired,
    unpaidList: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CommissionHourlyLimitOvertimePanel;
